// /src/pages/StoryApp.js  (STUDENT VERSION, UPDATED)
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getAuth, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  increment,
  serverTimestamp,
  arrayRemove,
  arrayUnion,
} from "firebase/firestore";
import { db as fdb, callFunction, } from "../firebase";

import {
  getStorage,
  ref as storageRef,
  deleteObject,
} from "firebase/storage";

import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Tooltip,
  CircularProgress,
  Chip,
  Modal,
  Box,
  Switch,
} from "@mui/material";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import VoiceRecorder from "../components/VoiceRecorder";

/* ---------------- Helper: log story read for students ---------------- */
export const logStoryRead = async (studentId, readingTimeInMinutes = 1) => {
  const studentRef = doc(fdb, "students", studentId);
  const today = new Date().toISOString().slice(0, 10);

  await setDoc(
    studentRef,
    {
      storiesGenerated: increment(1),
      totalReadingTime: increment(readingTimeInMinutes),
      lastActive: serverTimestamp(),
    },
    { merge: true }
  );

  const activityRef = doc(fdb, "students", studentId, "activity", today);
  await setDoc(activityRef, { read: true }, { merge: true });
};

/* ---------------- Small utils (kept from your file) ---------------- */
const chunkText = (text, maxLength) => {
  const chunks = [];
  for (let i = 0; i < text.length; i += maxLength) {
    chunks.push(text.slice(i, i + maxLength));
  }
  return chunks;
};

const formatStory = (storyText) =>
  String(storyText || "")
    .split("\n")
    .map((line, idx) => (
      <Typography
        key={idx}
        style={{
          fontSize: "1.2rem",
          marginBottom: "10px",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {line}
      </Typography>
    ));

const cleanWordForLookup = (w) =>
  String(w || "")
    .trim()
    .replace(/[^\p{L}\p{N}'-]/gu, "") // keep letters/numbers/'/-
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const formatStoryClickable = (storyText, onWordClick) =>
  String(storyText || "")
    .split("\n")
    .map((line, idx) => {
      const words = line.split(" ").filter(Boolean);

      return (
        <Typography
          key={idx}
          style={{
            fontSize: "1.2rem",
            marginBottom: "10px",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {words.map((w, i) => (
            <span
              key={`${idx}-${i}-${w}`}
              onClick={() => {
  console.log("[Dictionary] word clicked:", w);
  onWordClick(w);
}}

              style={{ cursor: "pointer", color: "inherit" }}
              title="Tap for meaning"
            >
              {w}{" "}
            </span>
          ))}
        </Typography>
      );
    });



const getQuestionCountForAge = (ageGroup = "6-9 years old") => {
  const ag = String(ageGroup || "").toLowerCase();

  if (ag.includes("3-5")) return 0;        // you already block questions for 3–5
  if (ag.includes("6-9")) return 5;
  if (ag.includes("10-13")) return 7;
  if (ag.includes("14+")) return 8;

  return 5; // default
};



/* ---------------- NEW: Save comprehension results (student + fallback) ---------------- */
async function saveComprehensionResult({
  uid,
  storyId = "",
  storyTitle = "",
  genre = "",
  correct = 0,
  total = 0,
  percent = 0,
}) {
  if (!uid) return;

  // Determine whether this account is a student (has a doc at /students/{uid})
  let isStudent = false;
  try {
    const studentSnap = await getDoc(doc(fdb, "students", uid));
    isStudent = studentSnap.exists();
  } catch {
    // default false
  }

  const rootPath = isStudent ? ["students", uid] : ["users", uid];

  // Subcollection (audit/trend)
  try {
    await addDoc(collection(fdb, ...rootPath, "comprehensionResults"), {
      storyId,
      storyTitle,
      genre,
      correct,
      total,
      score: percent,
      percent,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("[comprehension] subcollection write failed:", e?.message || e);
  }

  // Root rollup (feeds dashboards)
  try {
    await updateDoc(doc(fdb, ...rootPath), {
      comprehensionScores: arrayUnion({
        date: new Date().toISOString(),
        score: percent,
        storyTitle,
        genre,
      }),
      lastActive: serverTimestamp(),
    });
  } catch (e) {
    // If rollup updateDoc fails (doc might not exist for some edge cases), setDoc merge
    try {
      await setDoc(
        doc(fdb, ...rootPath),
        {
          comprehensionScores: [
            {
              date: new Date().toISOString(),
              score: percent,
              storyTitle,
              genre,
            },
          ],
          lastActive: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e2) {
      console.warn("[comprehension] rollup write failed:", e2?.message || e2);
    }
  }
}

/* ---------------- Component ---------------- */
const StoryApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const storyRef = useRef(null);
  const wordBankRef = useRef(null);
const questionsRef = useRef(null);
const recorderRef = useRef(null);
const [questionMode, setQuestionMode] = useState("multiple_choice");
const [constructedMarks, setConstructedMarks] = useState({});
const [questionsPanelOpen, setQuestionsPanelOpen] = useState(false);

const [enableWordTap, setEnableWordTap] = useState(false);
const [definitionOpen, setDefinitionOpen] = useState(false);
const [selectedWord, setSelectedWord] = useState("");
const [loadingDefinition, setLoadingDefinition] = useState(false);
const [wordDefinition, setWordDefinition] = useState(null);
const [definitionError, setDefinitionError] = useState("");


  const [diversityMode, setDiversityMode] = useState(
    () => localStorage.getItem("diversityMode") === "true"
  );

  const queryParams = new URLSearchParams(location.search);
  const selectedProfile = queryParams.get("profile");

  

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("❌ Error logging out:", error);
    }
  };

  // Back destination (used on both the first page and after reading)
 const backToDashboard = async () => {
  try {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, { state: { refresh: true } });
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      navigate("/");
      return;
    }

    const studentSnap = await getDoc(doc(fdb, "students", uid));
    if (studentSnap.exists()) {
      navigate("/student-dashboard", { state: { refresh: true } });
      return;
    }

    const teacherSnap = await getDoc(doc(fdb, "teachers", uid));
    if (teacherSnap.exists()) {
      navigate("/teacher-dashboard", { state: { refresh: true } });
      return;
    }

    navigate("/");
  } catch (e) {
    console.error("❌ Error resolving dashboard:", e);
    navigate("/");
  }
};

  // UI + form state
  const [formValues, setFormValues] = useState({
    curriculumTopic: "",
    genre: "",
    characters: [],
    setting: "",
    themes: [],
    themeInput: "",
    length: "",
    ageGroup: "",
    storyDescription: "",
    characterDetails: [{ name: "", age: "", description: "" }],
  });

  const [story, setStory] = useState("");
  const [storyTitle, setStoryTitle] = useState("");
  const [audioSrc, setAudioSrc] = useState("");
  const [generatedImage, setGeneratedImage] = useState("");
  const [loadingStory, setLoadingStory] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [comprehensionQuestions, setComprehensionQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [revealedAnswers, setRevealedAnswers] = useState(false);
  const [phonicsStory, setPhonicsStory] = useState("");
  const [loadingPhonics, setLoadingPhonics] = useState(false);
  const [selectedPhonicsWord, setSelectedPhonicsWord] = useState(null);
  const [score, setScore] = useState(0);
    // ---------------- VOCAB / WORD BANK ----------------
 
  const [loadingVocab] = useState(false);

  const [vocabulary, setVocabulary] = useState([]);
  const [showWordBank, setShowWordBank] = useState(false);

  const [readingStartTime, setReadingStartTime] = useState(null);

  const [showRecorder, setShowRecorder] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState("");
  const [recordings, setRecordings] = useState([]);
  const [showManageRecordingsModal, setShowManageRecordingsModal] = useState(false);
  const [showThemeLoadedBanner, setShowThemeLoadedBanner] = useState(false);

  const genreOptions = ["Fantasy", "Adventure", "Mystery", "Comedy", "Horror", "Suspense", "Action"];
  const themeOptions = ["Friendship", "Courage", "Family", "Magic"];
  const lengthOptions = ["Short", "Medium", "Long"];
  const ageGroupOptions = ["3-5 years old", "6-9 years old", "10-13 years old", "14+ years old"];

  // Load existing recordings to enforce 3-limit (students)
 useEffect(() => {
  const fetchRecordings = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const studentDoc = await getDoc(doc(fdb, "students", user.uid));
      if (studentDoc.exists()) {
        const s = studentDoc.data();
        const recs = Array.isArray(s.recordings) ? s.recordings : [];
        setRecordings(recs);
      }
    } catch (e) {
      console.error("Error fetching recordings:", e);
    }
  };

  fetchRecordings();
}, [auth.currentUser]);

  // Theme injection from teacher
  useEffect(() => {
    if (location.state?.theme) {
      const { theme, setting, description } = location.state.theme;
      setFormValues((prev) => ({
        ...prev,
        setting: setting || "",
        storyDescription: description || "",
        themes: theme ? [theme] : [],
      }));
      setShowThemeLoadedBanner(true);
      setTimeout(() => setShowThemeLoadedBanner(false), 4000);
    }
  }, [location.state?.theme]);

  // Reading timer
  useEffect(() => {
    if (story) setReadingStartTime(Date.now());
  }, [story]);


const handleFinishReading = useCallback(async () => {
  try {
    if (!readingStartTime || !auth.currentUser) return;

    const uid = auth.currentUser.uid;
    const studentRef = doc(fdb, "students", uid);
    const userRef = doc(fdb, "users", uid);

    const [studentSnap, userSnap] = await Promise.all([getDoc(studentRef), getDoc(userRef)]);
    const isStudent = studentSnap.exists();
    const base = isStudent ? studentSnap.data() : userSnap.data();
    if (!base) return;

    const elapsed = Math.round((Date.now() - readingStartTime) / 1000);
    const today = new Date().toISOString().split("T")[0];

    let activity = Array.isArray(base.readingActivity) ? [...base.readingActivity] : [];
    const idx = activity.findIndex((e) => e.date === today);
    if (idx >= 0) {
      activity[idx].timeSpent = Number(activity[idx].timeSpent || 0) + elapsed;
      activity[idx].storiesGenerated = Number(activity[idx].storiesGenerated || 0) + 1;
    } else {
      activity.push({ date: today, storiesGenerated: 1, timeSpent: elapsed });
    }

    const genres = { ...(base.favoriteGenres || {}), ...(base.genresExplored || {}) };
    if (formValues.genre) {
      genres[formValues.genre] = Number(genres[formValues.genre] || 0) + 1;
    }

    const lastReadDate = base.lastReadDate ? new Date(base.lastReadDate) : null;
    const oneDay = 86400000;
    let streak = Number(base.readingStreak || 0);
    if (lastReadDate) {
      const diff = Math.floor((Date.now() - lastReadDate.getTime()) / oneDay);
      streak = diff === 1 ? streak + 1 : 1;
    } else {
      streak = 1;
    }

    const updates = {
      readingActivity: activity,
      favoriteGenres: genres,
      genresExplored: genres,
      readingStreak: streak,
      lastReadDate: today,
      storiesRead: Number(base.storiesRead || 0) + 1,
      totalReadingTime: Number(base.totalReadingTime || 0) + elapsed,
      lastRead: new Date().toISOString(),
    };

    if (isStudent) {
      await setDoc(studentRef, updates, { merge: true });
      await setDoc(studentRef, { storiesGenerated: increment(1), lastActive: serverTimestamp() }, { merge: true });
      await setDoc(doc(fdb, "students", uid, "activity", today), { read: true }, { merge: true });
    } else {
      await setDoc(userRef, updates, { merge: true });
    }
  } catch (e) {
    console.error("handleFinishReading error:", e);
  }
}, [readingStartTime, auth, formValues.genre]);

  // Auto-save after 2 minutes
  useEffect(() => {
    if (story && !audioSrc && readingStartTime) {
      const t = setTimeout(() => handleFinishReading(), 120000);
      return () => clearTimeout(t);
    }
  }, [story, readingStartTime, audioSrc, handleFinishReading]);

  // Save on unload
  useEffect(() => {
  const beforeUnload = () => handleFinishReading();
  window.addEventListener("beforeunload", beforeUnload);
  return () => window.removeEventListener("beforeunload", beforeUnload);
}, [handleFinishReading]);


const scrollToRef = (ref) => {
  if (!ref?.current) return;
  ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
};


  /* ---------------- Phonics + TTS helpers ---------------- */
  const speakWord = async (word = "") => {
    try {
      const processedWord = word.trim().replace(/[^a-zA-Z]/g, "");
      const response = await fetch("https://storytelling-backend-i2pl.onrender.com/tts_phonics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word: processedWord }),
      });
      if (!response.ok) throw new Error("TTS request failed");
      const audioBlob = await response.blob();
      new Audio(URL.createObjectURL(audioBlob)).play();
    } catch (e) {
      console.error("Phonics TTS error:", e);
    }
  };


  // 🔵 Inline phonics: highlight words in the main story line
const renderStoryInlinePhonics = (
  storyText,
  phonicsText,
  speakWord,
  setSelectedPhonicsWord
) => {
  const rules = {
    ai: "darkblue", ay: "darkblue", ee: "darkblue", ea: "darkblue",
    ie: "darkblue", oa: "darkblue", oe: "darkblue", ue: "darkblue",
    ow: "darkblue", ou: "darkblue", oi: "darkblue", oy: "darkblue",
    ar: "darkblue", er: "darkblue", ir: "darkblue", or: "darkblue", ur: "darkblue",
    sh: "darkblue", ch: "darkblue", th: "darkblue", wh: "darkblue", ph: "darkblue",
    bl: "darkblue", cl: "darkblue", fl: "darkblue", gl: "darkblue", pl: "darkblue", sl: "darkblue",
    br: "darkblue", cr: "darkblue", dr: "darkblue", fr: "darkblue", gr: "darkblue", pr: "darkblue", tr: "darkblue",
    sc: "darkblue", sk: "darkblue", sm: "darkblue", sn: "darkblue", sp: "darkblue", st: "darkblue", sw: "darkblue",
    qu: "darkblue",
  };

  const clean = (t) =>
    String(t || "")
      .replace(/\[.*?\]/g, "")
      .replace(/<.*?>/g, "")
      .trim();

  const storyLines = clean(storyText)
    .replace(/\.\s+/g, ".\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const phonicsLines = clean(phonicsText)
    .replace(/\.\s+/g, ".\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  return storyLines.map((line, idx) => {
    const storyWords = line.split(" ").filter(Boolean);
    const phonicsWords = (phonicsLines[idx] || "").split(" ").filter(Boolean);

    return (
      <Typography
        key={idx}
        style={{ fontSize: "1.2rem", marginBottom: "10px" }}
      >
        {storyWords.map((w, i) => {
          const phonicsWord = phonicsWords[i] || "";
          // backend phonics: **sh**ip, ad-ven-ture, etc.
          const syllableForm = phonicsWord.replace(/\*\*/g, ""); // keep dashes
          const lower = syllableForm.toLowerCase();
          const hasMarkers = phonicsWord.includes("**");
          const matchedPattern = Object.keys(rules).find((r) =>
            lower.includes(r)
          );
          const isPhonicsWord = hasMarkers || !!matchedPattern;

          // Find pattern between ** ** if present, e.g. **sh**
let focusPattern = null;
const starMatch = phonicsWord.match(/\*\*(.+?)\*\*/);
if (starMatch && starMatch[1]) {
  focusPattern = starMatch[1]; // e.g. "sh"
} else if (matchedPattern) {
  focusPattern = matchedPattern; // fallback to our local rules
}


          const color = isPhonicsWord
            ? rules[matchedPattern] || "#004d40"
            : "#111";

          const cleanForAudio = syllableForm.replace(/[^a-zA-Z]/g, "");

          return (
            <span
              key={`${w}-${i}`}
              onClick={() => {
                if (cleanForAudio) {
                  speakWord(cleanForAudio);
                }
                if (isPhonicsWord) {
                  setSelectedPhonicsWord({
  display: w.replace(/[^a-zA-Z'-]/g, ""),
  syllables: syllableForm || w,
  pattern: focusPattern || null,
});

                }
              }}
              style={{
                color,
                fontWeight: isPhonicsWord ? "bold" : "normal",
                cursor: "pointer",
                marginRight: 5,
                textDecoration: isPhonicsWord ? "underline" : "none",
              }}
            >
              {w}
            </span>
          );
        })}
      </Typography>
    );
  });
};

const fetchWordDefinition = async (rawWord) => {
console.log("[Dictionary] fetchWordDefinition called with:", rawWord);

  try {
    const word = cleanWordForLookup(rawWord);
    if (!word) return;

    setSelectedWord(word);
    setDefinitionError("");
    setWordDefinition(null);
    setLoadingDefinition(true);
    setDefinitionOpen(true);

    // 1) Normal dictionary
    const r = await fetch(
      `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`
    );
    if (!r.ok) throw new Error("Not found");

    const data = await r.json();
    const entry = data?.[0];
    const meaning = entry?.meanings?.[0];
    const defObj = meaning?.definitions?.[0];

    const rawDefinition = defObj?.definition || "";

    // ✅ Debug: confirm what we’re simplifying
    console.log("[Dictionary] word:", word);
    console.log("[Dictionary] rawDefinition:", rawDefinition);

    // 2) Simplify (Firebase Function)
    const rawRes = await callFunction("simplifyDefinitionHttp", {
      word,
      definition: rawDefinition,
      ageGroup: formValues.ageGroup || "6-9 years old",
    });

    // ✅ normalise result shape
    const simplifiedJson =
      rawRes?.data && typeof rawRes.data === "object" ? rawRes.data : rawRes;

    console.log("[Dictionary] simplifyDefinitionHttp result:", simplifiedJson);
    console.log("[Dictionary] simpleDefinition:", simplifiedJson?.simpleDefinition);

    setWordDefinition({
      partOfSpeech: meaning?.partOfSpeech || "",
      definition: simplifiedJson?.simpleDefinition || rawDefinition,
      example: simplifiedJson?.simpleExample || defObj?.example || "",
      synonyms: defObj?.synonyms || meaning?.synonyms || [],
      // optional debug flag if you want it later
      isSimplified: !!simplifiedJson?.simpleDefinition,
    });
  } catch (e) {
    console.error("[Dictionary] error:", e);
    setDefinitionError("Sorry — I couldn’t find a definition for that word.");
  } finally {
    setLoadingDefinition(false);
  }
};


  
  const handleGeneratePhonics = async () => {
    if (!story) return alert("Generate a story first.");
    setLoadingPhonics(true);
    setSelectedPhonicsWord(null);
    try {
      const cleaned = String(story).replace(/\[.*?\]/g, "").replace(/<.*?>/g, "").trim();
      const response = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_phonics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story: cleaned, ageGroup: formValues.ageGroup || "default" }),
      });
      if (!response.ok) throw new Error("Failed to fetch phonics story");
      const data = await response.json();
      setPhonicsStory(String(data.phonicsStory || "").trim());
    } catch (e) {
      console.error("Phonics error:", e);
    } finally {
      setLoadingPhonics(false);
    }
  };

  /* ---------------- Comprehension Q&A ---------------- */
const handleGenerateQuestions = async () => {
    if (!story) return alert("Generate a story first.");
    setLoadingQuestions(true);
    try {
      const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_questions", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
  story,
  ageGroup: formValues.ageGroup || "6-9 years old",
  questionMode,
  numQuestions: getQuestionCountForAge(formValues.ageGroup || "6-9 years old"),
}),

});

if (!resp.ok) throw new Error("Failed to fetch questions");

      const data = await resp.json();
      setComprehensionQuestions(data.comprehensionQuestions || []);
      setCorrectAnswers(data.correctAnswers || []);
      setSelectedAnswers({});
      setConstructedMarks({});
      setRevealedAnswers(false);
      setScore(0);
    } catch (e) {
      console.error("Questions error:", e);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleMarkConstructedAnswer = async (index) => {
  try {
    const qObj = comprehensionQuestions[index];
    const answerText = String(selectedAnswers[index] || "").trim();

    if (!answerText) {
      alert("Please write an answer first.");
      return;
    }

    if (!qObj?.answerGuidance) {
      alert("Mark scheme missing for this question.");
      return;
    }

    const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/mark_constructed_answer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        answer: answerText,
        answerGuidance: qObj.answerGuidance,
      }),
    });

    if (!resp.ok) throw new Error("Marking failed");
    const data = await resp.json();

    setConstructedMarks((prev) => ({ ...prev, [index]: data }));
  } catch (e) {
    console.error("Constructed marking error:", e);
    alert("Could not mark the answer. Please try again.");
  }
};

const getConstructedSummary = () => {
  // Only count questions that actually have a mark
  const marks = comprehensionQuestions
    .map((_, i) => constructedMarks[i])
    .filter(Boolean);

  if (!marks.length) {
    return { totalScore: 0, totalMax: 0, percent: 0, answeredCount: 0, totalQuestions: comprehensionQuestions.length };
  }

  const totalScore = marks.reduce((s, m) => s + Number(m.score || 0), 0);
  const totalMax = marks.reduce((s, m) => s + Number(m.maxScore || 0), 0);
  const percent = totalMax ? Math.round((totalScore / totalMax) * 100) : 0;

  return {
    totalScore,
    totalMax,
    percent,
    answeredCount: marks.length,
    totalQuestions: comprehensionQuestions.length,
  };
};

const handleSaveConstructedScore = async () => {
  const summary = getConstructedSummary();

  if (!comprehensionQuestions.length) {
    alert("No questions to score yet.");
    return;
  }

  // Require all questions to be marked before saving
  if (summary.answeredCount < comprehensionQuestions.length) {
    alert("Please mark all answers before saving your score.");
    return;
  }

  const uidLocal = auth.currentUser?.uid;

  // Save as a % score (keeps dashboards consistent)
  await saveComprehensionResult({
    fdb,
    uid: uidLocal,
    storyId: "",
    storyTitle: storyTitle || "Untitled",
    genre: formValues.genre || "",
    correct: summary.totalScore, // using points
    total: summary.totalMax,     // using max points
    percent: summary.percent,
  });

  toast.success(`✅ Saved written score: ${summary.percent}%`);
};


  const handleRevealAnswers = async () => {
    let correct = 0;
    comprehensionQuestions.forEach((_, i) => {
      const ua = (selectedAnswers[i] || "").trim().toUpperCase();
      const ca = (correctAnswers[i] || "").trim().toUpperCase();
      if (ua && ca && ua === ca) correct++;
    });
    setScore(correct);
    setRevealedAnswers(true);

    // ✅ Persist comprehension
    try {
      const total = comprehensionQuestions.length || 0;
      const percent = total ? Math.round((correct / total) * 100) : 0;
      const uid = auth.currentUser?.uid;
      await saveComprehensionResult({
        uid,
        storyId: "", // supply if you have one
        storyTitle: storyTitle || "Untitled",
        genre: formValues.genre || "",
        correct,
        total,
        percent,
      });
    } catch (e) {
      console.warn("Could not save comprehension:", e?.message || e);
    }
  };

  /* ---------------- TTS ---------------- */
  const generateTTS = async (storyText) => {
    try {
      if (!storyText) return;
      if (storyText.length > 3000) {
        const textChunks = chunkText(storyText, 3000);
        const blobs = [];
        for (const chunk of textChunks) {
          const r = await fetch("https://storytelling-backend-i2pl.onrender.com/tts", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: chunk }),
          });
          if (!r.ok) throw new Error("TTS chunk failed");
          blobs.push(await r.blob());
        }
        const merged = new Blob(blobs, { type: "audio/mpeg" });
        setAudioSrc(URL.createObjectURL(merged));
      } else {
        const r = await fetch("https://storytelling-backend-i2pl.onrender.com/tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: storyText }),
        });
        if (!r.ok) throw new Error("TTS failed");
        const audioBlob = await r.blob();
        setAudioSrc(URL.createObjectURL(audioBlob));
      }

      const uid = auth.currentUser?.uid;
      if (uid) {
        // log TTS usage on the student or user doc
        const studentSnap = await getDoc(doc(fdb, "students", uid));
        const ref = studentSnap.exists() ? doc(fdb, "students", uid) : doc(fdb, "users", uid);
        await updateDoc(ref, { ttsPlaybacks: increment(1), lastActive: serverTimestamp() });

        const today = new Date().toISOString().slice(0, 10);
        if (studentSnap.exists()) {
          await setDoc(doc(fdb, "students", uid, "activity", today), { ttsPlayed: true }, { merge: true });
        }
      }
    } catch (e) {
      console.error("❌ Error generating TTS:", e);
    }
  };

  /* ---------------- Recordings ---------------- */
  const handleDeleteRecording = async (rec) => {
    try {
      const storage = getStorage();
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      await deleteObject(storageRef(storage, rec.fileName));
      const studentRef = doc(fdb, "students", uid);
      await updateDoc(studentRef, { recordings: arrayRemove(rec) });
      const updated = recordings.filter((r) => r.fileName !== rec.fileName);
      setRecordings(updated);
      if (updated.length < 3) {
        setShowManageRecordingsModal(false);
      }
      alert("✅ Recording deleted successfully!");
    } catch (e) {
      console.error("❌ Failed to delete recording:", e);
      alert("Something went wrong while deleting the recording.");
    }
  };

  /* ---------------- Inputs ---------------- */
  

const handleInputChange = (e) =>
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleThemeChange = (e) => {
    const v = e.target.value;
    if (!formValues.themes.includes(v)) {
      setFormValues((prev) => ({ ...prev, themes: [...prev.themes, v] }));
    }
  };

  const handleAddTheme = () => {
    const t = formValues.themeInput.trim();
    if (!t) return;
    setFormValues((prev) => ({ ...prev, themes: [...prev.themes, t], themeInput: "" }));
  };

  const handleRemoveTheme = (i) => {
    const copy = [...formValues.themes];
    copy.splice(i, 1);
    setFormValues((prev) => ({ ...prev, themes: copy }));
  };

  const handleCharacterDetailChange = (i, field, value) => {
    const copy = [...formValues.characterDetails];
    copy[i][field] = value;
    setFormValues((prev) => ({ ...prev, characterDetails: copy }));
  };

  const addCharacter = () => {
    setFormValues((prev) => ({
      ...prev,
      characterDetails: [...prev.characterDetails, { name: "", age: "", description: "" }],
    }));
  };

  const resetForm = () => {
    setFormValues({
      curriculumTopic: "",
      genre: "",
      characters: [],
      setting: "",
      themes: [],
      themeInput: "",
      length: "",
      ageGroup: "",
      storyDescription: "",
      characterDetails: [{ name: "", age: "", description: "" }],
    });
    setStory("");
    setStoryTitle("");
    setAudioSrc("");
    setGeneratedImage("");
    setComprehensionQuestions([]);
    setPhonicsStory("");
    setShowRecorder(false);
    setShowPlayback(false);
    setRecordedAudioUrl("");
    setReadingStartTime(null);
    setSelectedPhonicsWord(null);
  };

  /* ---------------- Story generation ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      console.error("❌ No authenticated user.");
      return;
    }

    if (!formValues.genre || !formValues.length || !formValues.ageGroup) {
      alert("Please complete the mandatory fields: Genre, Story Length, and Age Group.");
      return;
    }

    // Word count hints
    const hints = {
      "3-5 years old": {
        short: "Make sure the story is exactly 50 words.",
        medium: "Keep the story between 150 and 250 words.",
        long: "Keep the story between 400 and 600 words.",
      },
      "6-9 years old": {
        short: "Make sure the story is around 150 words.",
        medium: "Keep the story between 250 and 350 words.",
        long: "Keep the story between 400 and 600 words.",
      },
      "10-13 years old": {
        short: "Make sure the story is around 250 words.",
        medium: "Keep the story between 350 and 550 words.",
        long: "Keep the story between 600 and 800 words.",
      },
      "14+ years old": {
        short: "Make sure the story is around 250 words.",
        medium: "Keep the story between 400 and 600 words.",
        long: "Keep the story between 800 and 950 words.",
      },
    };
    const lengthKey = String(formValues.length || "").toLowerCase();
    const wordRule = hints[formValues.ageGroup]?.[lengthKey] || "";

    let customPrefix = "";
    if (formValues.ageGroup === "3-5 years old") {
      customPrefix = `
You are writing a story for a child aged 3 to 5 years old.

✅ STRICT RULES:
- Each sentence MUST have between 9 and 12 words.
- DO NOT use fewer than 9 or more than 12 words.
- Each word should be VERY simple and ideally 1–4 letters long.
- Use basic phonics words: cat, dog, run, jump, fun, fish, etc.
- The story MUST NOT exceed 50 words TOTAL.
- Keep it friendly and playful.
- Do NOT use colons, semi-colons, or hyphens to link ideas.
- Do NOT wrap the story in quotation marks or extra formatting.

Use one short sentence per line.

At the start of the story, include a clear title on the first line, like this:
Title: <Your Story Title>

${wordRule}`.trim();
    } else {
      customPrefix = `${wordRule}`;
    }

    // Resolve setting/characters/themes, diversity
    const setting =
      formValues.setting.trim() ||
      (diversityMode ? "A vibrant cultural setting from a real-world region" : "A general adventure setting");

    const characters =
      formValues.characterDetails && formValues.characterDetails.length
        ? formValues.characterDetails
        : diversityMode
        ? [{ name: "A mix of characters from various cultures and backgrounds" }]
        : [{ name: "A mix of regular characters" }];

    const themes =
      formValues.themes && formValues.themes.length
        ? formValues.themes
        : diversityMode
        ? ["Diversity, Inclusion, Cultural Exchange"]
        : ["Friendship, Adventure, Learning"];

    // Pre-reset UI
    setComprehensionQuestions([]);
    setLoadingStory(true);
    setLoadingImage(true);
    setLoadingAudio(false);
    setStory("");
    setShowRecorder(false);
    setShowPlayback(false);
    setRecordedAudioUrl("");
    setStoryTitle("");
    setAudioSrc("");
    setGeneratedImage("");
    setPhonicsStory("");
    setVocabulary([]);
    setShowWordBank(false);
    setQuestionMode("multiple_choice");
    setConstructedMarks({});



    // Country & variant
    let userCountry = "Unknown";
    let languageVariant = "US English";
    try {
      userCountry = await fetch("https://ipapi.co/json/")
        .then((r) => r.json())
        .then((j) => j.country_name)
        .catch(() => "Unknown");
      if (userCountry === "United Kingdom") languageVariant = "UK English";
    } catch {
      // ignore
    }

    const payload = {
      genre: formValues.genre,
      setting,
      themes,
      length: formValues.length,
      ageGroup: formValues.ageGroup,
      storyDescription: `${customPrefix}\n\n${formValues.storyDescription || "An adventure story."}`.trim(),
      characterDetails: characters,
      diversityMode,
      curriculumTopic: formValues.curriculumTopic || "General",
      userCountry,
      languageVariant,
    };

    // Update profile counters if needed (parent's user doc)
    try {
      const uid = auth.currentUser.uid;
      const userRef = doc(fdb, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && selectedProfile) {
        const base = userSnap.data() || {};
        const profiles = (base.familyProfiles || []).map((p) => {
          if (p.name === selectedProfile) {
            const fg = { ...(p.favoriteGenres || {}) };
            if (formValues.genre) fg[formValues.genre] = (fg[formValues.genre] || 0) + 1;
            return {
              ...p,
              storiesGenerated: Number(p.storiesGenerated || 0) + 1,
              favoriteGenres: fg,
              recentActivity: [...(p.recentActivity || []), new Date().toISOString()],
            };
          }
          return p;
        });
        await setDoc(userRef, { familyProfiles: profiles }, { merge: true });
      }
    } catch (e) {
      console.warn("Profile stat update skipped:", e?.message || e);
    }

    // Generate story
    let storyData = {};
    try {
      const storyResp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!storyResp.ok) throw new Error("Failed to generate story");
      storyData = await storyResp.json();
      setStory(storyData.story || "");
setStoryTitle(storyData.title || "Untitled Story");

// ✅ Word Bank comes from /generate response (like StoryAppUser)
setVocabulary(Array.isArray(storyData.vocabulary) ? storyData.vocabulary : []);

// quick debug (remove later)
console.log("vocab returned:", storyData.vocabulary);


      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (eAll) {
      console.error("❌ Error generating story:", eAll);
    } finally {
      setLoadingStory(false);
    }

    // TTS
    if (storyData?.story) {
      await generateTTS(storyData.story);
    }

    // Image
    try {
      const imgResp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: setting }),
      });
      if (imgResp.ok) {
        const imgData = await imgResp.json();
        setGeneratedImage(imgData.image_url);
      } else {
        setGeneratedImage("/images/default.png");
      }
    } catch {
      setGeneratedImage("/images/default.png");
    }

    setLoadingImage(false);
  };




  /* ---------------- Navigation ---------------- */
  const handleNavigation = async (path) => {
    await handleFinishReading();
    navigate(path);
  };

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "repeat-y",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{ height: 40, cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
        <div style={{ display: "flex", gap: 12 }}>
          {/* ✅ Back to Dashboard on the first page too (consistency with parent app) */}
          <Button
            variant="outlined"
            onClick={backToDashboard}
            style={{ color: "white", borderColor: "white" }}
          >
            Back to Dashboard
          </Button>
          <Button onClick={handleLogout} className="header-button" color="inherit">
            Logout
          </Button>
        </div>
      </header>

      {/* Intro */}
      {!story && (
<Container maxWidth="md" style={{ marginTop: "20px" }}>
  {/* Instructions Section */}
  <Card
    style={{
      marginBottom: "20px",
      padding: "20px",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      color: "white",
      borderRadius: "10px",
    }}
  >
    <CardContent>
      <Typography
        variant="h4"
        style={{
          textAlign: "center",
          marginBottom: "10px",
          fontWeight: "bold",
        }}
      >
        Welcome to YourStoryWorld!
      </Typography>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        To create your personalized story, you can fill out the fields below to shape the adventure to your liking. 
        But don’t worry—if you’d prefer to jump straight into a story, just complete the{" "}
        <span style={{ fontWeight: "bold" }}>minimum mandatory fields</span>, and we’ll take care of the rest!
      </Typography>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        Here’s what you can do:
      </Typography>
      <ul style={{ marginLeft: "20px" }}>
        <li>
          <span style={{ fontWeight: "bold" }}>Genre (Mandatory):</span> Choose the type of story you’d like to explore, such as adventure, fantasy, or mystery.
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Setting:</span> Decide where your story takes place—a magical forest, a bustling city, or even outer space!
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Story Length (Mandatory):</span> Select how long you’d like your story to be (short, medium, or long).
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Age Group (Mandatory):</span> Let us know who the story is for so we can adjust the language and content to suit the audience.
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Story Description:</span> Provide a brief idea or theme for your story, like "a pirate searching for treasure" or "a scientist discovering a new planet."
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Characters:</span> Add details about the characters you want in the story, including names, traits, or roles.
        </li>
      </ul>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        Once you've completed your choices, click <span style={{ fontWeight: "bold" }}>Generate Story</span> to watch your personalized adventure come to life! 
        If you'd rather skip the details, just fill in the mandatory fields to enjoy a ready-made story in seconds. You can even listen to your story with our audio playback feature. 🎧
      </Typography>
      <Typography
        style={{
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        Enjoy your storytelling journey! 🌟
      </Typography>
    </CardContent>
  </Card>
</Container>
)}

      <Container maxWidth="md" style={{ marginTop: 20 }}>
        {!story ? (
          <>
            {showThemeLoadedBanner && (
              <div
                style={{
                  backgroundColor: "#f3ffe5",
                  border: "2px solid #76c043",
                  color: "#2e7d32",
                  padding: "10px 15px",
                  borderRadius: "10px",
                  marginBottom: "20px",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "16px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                }}
              >
                ✨ Your teacher's theme was loaded successfully!
              </div>
            )}

            <Card
              style={{
                marginBottom: 20,
                padding: 20,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: 10,
                maxWidth: 600,
                margin: "auto",
              }}
            >
              <CardContent>
                {location.state?.theme && (
                  <div
                    style={{
                      backgroundColor: "#e6f4ea",
                      border: "2px solid #3cba54",
                      padding: "15px",
                      borderRadius: "10px",
                      marginBottom: "20px",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Typography variant="h6" style={{ color: "#2e7d32", marginBottom: 8, fontWeight: "bold" }}>
                      ✨ Your teacher assigned a theme!
                    </Typography>
                    <Typography variant="body1" style={{ fontSize: 15, color: "#1b5e20" }}>
                      <strong>Theme:</strong> {location.state.theme.theme}
                    </Typography>
                    <Typography variant="body1" style={{ fontSize: 15, color: "#1b5e20" }}>
                      <strong>Setting:</strong> {location.state.theme.setting}
                    </Typography>
                    <Typography variant="body1" style={{ fontSize: 15, color: "#1b5e20" }}>
                      <strong>Description:</strong> {location.state.theme.description}
                    </Typography>
                  </div>
                )}

                <Typography variant="h4" style={{ textAlign: "center", marginBottom: 20 }}>
                  Create Your Story
                </Typography>

                {/* Mandatory */}
                <Typography
                  variant="h6"
                  style={{ marginBottom: 10, fontWeight: "bold", textDecoration: "underline" }}
                >
                  Mandatory Fields
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Tooltip title="Required. Choose your story type." arrow>
                        <TextField
                          select
                          fullWidth
                          name="genre"
                          label="Genre *"
                          value={formValues.genre}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {genreOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip title="Required. Select story length." arrow>
                        <TextField
                          select
                          fullWidth
                          name="length"
                          label="Story Length *"
                          value={formValues.length}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {lengthOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip title="Required. Tailors language and content to the audience." arrow>
                        <TextField
                          select
                          fullWidth
                          name="ageGroup"
                          label="Age Group *"
                          value={formValues.ageGroup}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {ageGroupOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                    </Grid>
                  </Grid>

                  {/* Optional */}
                  <Typography
                    variant="h6"
                    style={{ marginTop: 20, marginBottom: 10, fontWeight: "bold", textDecoration: "underline" }}
                  >
                    Optional Fields
                  </Typography>

                  <Grid item xs={12} style={{ marginBottom: 10 }}>
                    <Typography variant="h6" style={{ marginBottom: 10 }}>
                      Diversity Mode:
                    </Typography>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={diversityMode}
                        onChange={() => {
                          const nv = !diversityMode;
                          localStorage.setItem("diversityMode", String(nv));
                          setDiversityMode(nv);
                        }}
                        style={{ transform: "scale(1.3)", cursor: "pointer", marginRight: 10 }}
                      />
                      <span style={{ fontSize: 14, fontWeight: "bold" }}>Enable Diversity Mode</span>
                    </div>
                    <p style={{ fontSize: 12, color: "#555", marginTop: 5 }}>
                      Make characters, settings and themes reflect diverse cultures and backgrounds.
                    </p>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Tooltip title="Optional. Choose where the story takes place, a location, country, a setting etc." arrow>
                        <TextField
                          fullWidth
                          name="setting"
                          label="Setting"
                          placeholder="E.g., A forest or castle or a bustling marketplace in Morocco"
                          value={formValues.setting}
                          onChange={handleInputChange}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip title="Optional. Pick a theme to include." arrow>
                        <TextField
                          select
                          fullWidth
                          name="themes"
                          label="Select Theme"
                          value=""
                          onChange={handleThemeChange}
                        >
                          {themeOptions.map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip title="Optional. Add your own custom theme, then click Add." arrow>
                        <TextField
                          fullWidth
                          name="themeInput"
                          label="Or Add Your Own Custom Theme"
                          value={formValues.themeInput}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddTheme();
                            }
                          }}
                        />
                        <Button onClick={handleAddTheme} variant="contained" color="primary" style={{ marginTop: 10 }}>
                          Add Custom Theme
                        </Button>
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" style={{ marginBottom: 10 }}>
                        Selected Themes:
                      </Typography>
                      {formValues.themes.map((theme, i) => (
                        <Chip
                          key={i}
                          label={theme}
                          onDelete={() => handleRemoveTheme(i)}
                          style={{ marginRight: 5, marginBottom: 5 }}
                        />
                      ))}
                    </Grid>

                    <Grid item xs={12}>
                    <Tooltip title="Optional. Brief description to steer the story. You can include elements or facts you would like within the story or just state a high level plot for the story....you can even use famous story plots from your favourite books/movies!" arrow>
                      <div>
                       <TextField
  fullWidth
  multiline
  minRows={2}
  maxRows={6}
  name="storyDescription"
  label="Story Description"
  value={formValues.storyDescription}
  onChange={handleInputChange}
/>


                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" style={{ marginBottom: 10, color: "#111" }}>Character Details:</Typography>
                    {formValues.characterDetails.map((c, i) => (
                      <Grid container spacing={2} key={i} style={{ marginBottom: 10 }}>
                        <Grid item xs={12}>
                          <Tooltip title="Optional. Character name." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Name"
                                value={c.name}
                                onChange={(e) => handleCharacterDetailChange(i, "name", e.target.value)}
                                placeholder="E.g., Simon, Joanne or Aisha "
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={6}>
                          <Tooltip title="Optional. Character age." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Age"
                                value={c.age}
                                onChange={(e) => handleCharacterDetailChange(i, "age", e.target.value)}
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip title="Optional. You can describe the character physically, personality, their traits, role, or backstory about them." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={c.description}
                                onChange={(e) => handleCharacterDetailChange(i, "description", e.target.value)}
                                placeholder="E.g., A brave explorer from Egypt, a cheeky boy with brown curly hair"
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ))}
                    <Button onClick={addCharacter} variant="contained" color="primary" style={{ margin: "10px 10px 0 0" }}>
                      Add Another Character
                    </Button>
                  </Grid>
                  </Grid>

                  <Button
  type="submit"
  variant="contained"
  color="secondary"
  fullWidth
  disabled={loadingStory}
  style={{ marginTop: 20 }}
>
  {loadingStory ? (
    <CircularProgress size={24} color="inherit" />
  ) : (
    "Generate Story"
  )}
</Button>
                </form>
              </CardContent>
            </Card>
          </>
        ) : (
          // Story view
          <Card
            ref={storyRef}
            style={{
              marginTop: 20,
              padding: 20,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: 10,
            }}
          >
            <CardContent>
              <Typography variant="h4" style={{ fontWeight: "bold", marginBottom: 10 }}>
                {storyTitle}
              </Typography>

              {loadingImage ? (
                <CircularProgress style={{ display: "block", margin: "20px auto" }} />
              ) : (
                generatedImage && (
                  <img
                    src={generatedImage}
                    alt="Story Illustration"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 10,
                      marginBottom: 20,
                      maxHeight: 400,
                      objectFit: "contain",
                    }}
                  />
                )
              )}

              <Typography variant="subtitle1" style={{ fontStyle: "italic", marginBottom: 20 }}>
                Starring: {formValues.characterDetails.map((c) => c.name).filter(Boolean).join(", ")}
              </Typography>

              {phonicsStory && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <div
                    style={{
                      backgroundColor: "#e0f7fa",
                      padding: "8px 15px",
                      borderRadius: 20,
                      display: "inline-flex",
                      alignItems: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src="https://cdn-icons-png.flaticon.com/128/1827/1827427.png"
                      alt="Audio Icon"
                      style={{ width: 18, height: 18, marginRight: 8, opacity: 0.8 }}
                    />
                          <Typography
  variant="body2"
  style={{ color: "#00796b", fontWeight: "bold" }}
>
  Blue, underlined words show phonics sounds.  
  Tap a word to hear it and see how it breaks into syllables.
</Typography>


                  </div>
                </div>
              )}

            {phonicsStory && selectedPhonicsWord && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      marginBottom: 16,
    }}
  >
    <div
      style={{
        backgroundColor: "#e0f7fa",
        padding: "10px 16px",
        borderRadius: 12,
        maxWidth: 420,
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      }}
    >
      {(() => {
        const { display, syllables, pattern } = selectedPhonicsWord || {};

        let syllableNodes = syllables || "";

        if (pattern) {
          const regex = new RegExp(`(${pattern})`, "i");
          const segments = (syllables || "").split(regex);

          syllableNodes = segments.map((seg, idx) => {
            if (seg.toLowerCase() === pattern.toLowerCase()) {
              return (
                <span
                  key={idx}
                  style={{ color: "#00796b", fontWeight: "bold" }}
                >
                  {seg}
                </span>
              );
            }
            return <span key={idx}>{seg}</span>;
          });
        }

        return (
          <>
            <Typography
              variant="subtitle2"
              style={{ color: "#00796b", textTransform: "uppercase" }}
            >
              Focus word
            </Typography>

            <Typography
              variant="h6"
              style={{ fontWeight: "bold", marginTop: 4, marginBottom: 4 }}
            >
              {display}
            </Typography>

            <Typography
              variant="body1"
              style={{ fontSize: "1rem", marginBottom: 4 }}
            >
              {syllableNodes}
            </Typography>

            {pattern && (
              <Typography variant="caption" style={{ color: "#004d40" }}>
                Focus sound: <strong>{pattern}</strong>
              </Typography>
            )}
          </>
        );
      })()}
    </div>
  </div>
)}

{formValues.ageGroup !== "3-5 years old" && !phonicsStory && enableWordTap && (
  <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
    <div
      style={{
        backgroundColor: "#fff3cd",
        padding: "8px 14px",
        borderRadius: 12,
        maxWidth: 520,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="body2" style={{ color: "#7a5d00", fontWeight: "bold" }}>
        📖 Tip: Tap any word to see its meaning.
      </Typography>
    </div>
  </div>
)}



<div>
  {phonicsStory
    ? renderStoryInlinePhonics(
        String(story),
        String(phonicsStory),
        speakWord,
        setSelectedPhonicsWord
      )
    : formValues.ageGroup !== "3-5 years old" && enableWordTap
      ? formatStoryClickable(story, fetchWordDefinition)
      : formatStory(story)}
</div>



{/* =========================
    Actions (single clean stack)
   ========================= */}
{story && (
  <div
    style={{
      marginBottom: 18,
      padding: 12,
      borderRadius: 12,
      background: "rgba(245,245,245,0.95)",
      border: "1px solid rgba(0,0,0,0.08)",
    }}
  >
    {/* 1) Word meanings (6-9+ only, and only when phonics is NOT shown) */}
    {formValues.ageGroup !== "3-5 years old" && !phonicsStory && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <Button
          variant={enableWordTap ? "contained" : "outlined"}
          color="primary"
          onClick={() => setEnableWordTap((v) => !v)}
          style={{ minWidth: 210 }}
        >
          {enableWordTap ? "Word dictionary: ON" : "Word dictionary"}
        </Button>

        <Typography variant="body2" style={{ color: "#555" }}>
          Switch to turn on, then tap a word to see its meaning.
        </Typography>
      </div>
    )}

{/* 2) Word Bank */}
{(() => {

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        marginBottom: 10,
      }}
    >
      <Button
  variant="contained"
  color="primary"
  disabled={loadingVocab || !story}
  style={{ minWidth: 210 }}
 onClick={() => {
  const next = !showWordBank;
  setShowWordBank(next);
  if (next) setTimeout(() => scrollToRef(wordBankRef), 80);
}}

>
  {loadingVocab
    ? "Building Word Bank..."
    : showWordBank
    ? "Hide Word Bank"
    : Array.isArray(vocabulary) && vocabulary.length > 0
    ? "Show Word Bank"
    : "Show Word Bank"
}
</Button>


      <Typography variant="body2" style={{ color: "#555" }}>
    Key vocabulary words from this story to learn (with meanings and examples).
  </Typography>
</div>

  );
})()}


{/* 3) Comprehension Questions (not for 3-5) */}
{formValues.ageGroup !== "3-5 years old" && (
  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
    <Button
      variant="contained"
      color="primary"
      style={{ minWidth: 210 }}
      onClick={() => {
        const next = !questionsPanelOpen;
        setQuestionsPanelOpen(next);
        if (next) setTimeout(() => scrollToRef(questionsRef), 80);
      }}
    >
      {questionsPanelOpen ? "Hide Questions" : "Comprehension Questions"}
    </Button>

    <Typography variant="body2" style={{ color: "#555" }}>
      Test your understanding with quick multiple choice or written questions.
    </Typography>
  </div>
)}

    {/* 4) Start Reading Aloud */}
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <Button
  variant="contained"
  color="primary"
  style={{ minWidth: 210 }}
  onClick={() => {
    if (recordings.length >= 3) {
      setShowManageRecordingsModal(true);
      return;
    }

    setShowRecorder(true);

    // ✅ after it renders, scroll to the recorder panel
    setTimeout(() => scrollToRef(recorderRef), 80);
  }}
>
  Start Reading Aloud
</Button>


      <Typography variant="body2" style={{ color: "#555" }}>
        Record yourself reading this story so you can listen to it later! (max 3 recordings).
      </Typography>
    </div>
  </div>
)}





              {["3-5 years old"].includes(formValues.ageGroup) && (
                <Button variant="contained" color="secondary" onClick={handleGeneratePhonics} disabled={loadingPhonics} style={{ marginTop: 8 }}>
                  {loadingPhonics ? "Processing Phonics..." : "Show Phonics"}
                </Button>
              )}

              {/* Audio */}
              {loadingAudio && (
                <div style={{ marginTop: 30 }}>
                  <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10 }}>
                    📖 Story Playback
                  </Typography>
                  <Typography style={{ fontStyle: "italic" }}>Processing audio...</Typography>
                </div>
              )}
              {audioSrc && (
                <div style={{ marginTop: 30 }}>
                  <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10 }}>
                    📖 Story Playback
                  </Typography>
                  <audio controls style={{ width: "100%" }}>
                    <source src={audioSrc} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* Recorder */}
              {story && (
                <div ref={recorderRef} style={{ marginTop: 30 }}>
                  <Typography variant="h5" style={{ marginBottom: 10, fontWeight: "bold" }}>
                    Your Voice Recording
                  </Typography>

                  

                  {showRecorder && (
                    <div style={{ backgroundColor: "#f5f5f5", padding: 15, borderRadius: 10 }}>
                      <VoiceRecorder
                        recorderLabel="Read-Aloud Recorder"
                        storyTitle={storyTitle}
                        onRecordingComplete={(blobUrl, blob) => {
                          setRecordedAudioUrl(blobUrl);
                          
                          setShowPlayback(true);
                        }}
                      />
                    </div>
                  )}

                  {showPlayback && recordedAudioUrl && (
                    <div style={{ backgroundColor: "#e8f0fe", padding: 15, borderRadius: 10, marginTop: 10 }}>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        Playback:
                      </Typography>
                      <audio controls src={recordedAudioUrl} style={{ width: "100%" }} />
                    </div>
                  )}
                </div>
              )}

{/* Word Bank Panel (opened from Actions stack) */}
{showWordBank && vocabulary && vocabulary.length > 0 && (
  <div ref={wordBankRef}>
    <Card
      style={{
        marginTop: 12,
        padding: 16,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 10,
        color: "#111",
      }}
    >
      <CardContent>
        <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
          📚 Word Bank
        </Typography>

        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {vocabulary.map((w, idx) => (
            <li key={idx} style={{ marginBottom: 12 }}>
              <Typography style={{ fontWeight: "bold", color: "#111" }}>{w.word}</Typography>

              {w.definition && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>Meaning:</strong> {w.definition}
                </Typography>
              )}

              {w.exampleFromStory && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>In the story:</strong> “{w.exampleFromStory}”
                </Typography>
              )}

              {Array.isArray(w.synonyms) && w.synonyms.length > 0 && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>Similar words:</strong> {w.synonyms.join(", ")}
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  </div>
)}


{/* Comprehension Questions Panel (opened from Actions stack) */}
{questionsPanelOpen && story && formValues.ageGroup !== "3-5 years old" && (
  <div ref={questionsRef}>
    <Card
      style={{
        marginTop: 12,
        padding: 16,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 10,
        color: "#111",
      }}
    >
      <CardContent>
        <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
          Comprehension Questions
        </Typography>

        {/* Switch with labels either side + explanation */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Multiple choice
          </Typography>

          <Switch
            checked={questionMode === "constructed_response"}
            onChange={(e) =>
              setQuestionMode(e.target.checked ? "constructed_response" : "multiple_choice")
            }
          />

          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Written answers
          </Typography>

          <Typography variant="body2" style={{ color: "#555" }}>
            (Multiple choice = quick A/B/C • Written = type an answer and get it auto-marked)
          </Typography>
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateQuestions}
          disabled={loadingQuestions}
        >
          {loadingQuestions ? "Generating..." : `Generate ${getQuestionCountForAge(formValues.ageGroup)} Questions`}
        </Button>
      </CardContent>
    </Card>
  </div>
)}







              {comprehensionQuestions.length > 0 && (
                <Card style={{ marginTop: 20, padding: 20, backgroundColor: "rgba(255, 255, 255, 0.9)", borderRadius: 10 }}>
                  <CardContent>
                    <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
                      Comprehension Questions
                    </Typography>
                    <ul>
  {comprehensionQuestions.map((q, i) => {
    const isObject = q && typeof q === "object";

    // -----------------------------
    // Constructed response rendering
    // -----------------------------
    if (questionMode === "constructed_response" && isObject) {
      const mark = constructedMarks[i];

      return (
        <li key={i} style={{ marginBottom: 18 }}>
          <Typography variant="body1" style={{ fontWeight: "bold", color: "#111", marginBottom: 8 }}>
            {q.question || `Question ${i + 1}`}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Write your answer here..."
            value={selectedAnswers[i] || ""}
            onChange={(e) => setSelectedAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
            disabled={false}
          />

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button variant="contained" color="primary" onClick={() => handleMarkConstructedAnswer(i)}>
              Mark Answer
            </Button>
          </div>

          {mark && (
            <div style={{ marginTop: 10 }}>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#111" }}>
                Score: {mark.score}/{mark.maxScore}
              </Typography>
              <Typography variant="body2" style={{ color: "#111" }}>
                {mark.feedback}
              </Typography>
              {Array.isArray(mark.missing) && mark.missing.length > 0 && (
                <Typography variant="body2" style={{ color: "#555", marginTop: 6 }}>
                  <strong>Try to include:</strong> {mark.missing.join(", ")}
                </Typography>
              )}
            </div>
          )}
        </li>
      );
    }

    // -----------------------------
    // Multiple choice rendering (legacy)
    // -----------------------------
    const qText = String(q || "");
    return (
      <li key={i} style={{ marginBottom: 15 }}>
        <Typography variant="body1" style={{ fontWeight: "bold", color: "#111" }}>
          {qText.split("\n")[0]}
        </Typography>
        <div>
          {["A", "B", "C"].map((opt) => {
            const line = qText.split("\n").find((ln) => ln.startsWith(`${opt})`)) || `${opt})`;
            return (
              <label key={opt} style={{ display: "block", margin: "5px 0", color: "#111" }}>
                <input
                  type="radio"
                  name={`question-${i}`}
                  value={opt}
                  checked={selectedAnswers[i] === opt}
                  onChange={() => setSelectedAnswers((prev) => ({ ...prev, [i]: opt }))}
                  disabled={revealedAnswers}
                />{" "}
                {line}
              </label>
            );
          })}
        </div>
        {revealedAnswers && (
          <Typography
            variant="body2"
            style={{
              color: selectedAnswers[i] === correctAnswers[i] ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {selectedAnswers[i] === correctAnswers[i]
              ? "✅ Correct!"
              : `❌ Incorrect! The correct answer was: ${correctAnswers[i]}`}
          </Typography>
        )}
      </li>
    );
  })}
</ul>

{/* Constructed response summary + save */}
{questionMode === "constructed_response" && comprehensionQuestions.length > 0 && (
  <div style={{ marginTop: 10 }}>
    {(() => {
      const s = getConstructedSummary();
      return (
        <>
          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Marked: {s.answeredCount}/{s.totalQuestions} • Points: {s.totalScore}/{s.totalMax} • Score: {s.percent}%
          </Typography>

          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: 10 }}
            onClick={handleSaveConstructedScore}
            disabled={s.answeredCount < s.totalQuestions}
          >
            Save Written Score
          </Button>

          {s.answeredCount < s.totalQuestions && (
            <Typography variant="body2" style={{ color: "#555", marginTop: 6 }}>
              Mark all answers to enable saving.
            </Typography>
          )}
        </>
      );
    })()}
  </div>
)}


                    {questionMode === "multiple_choice" && !revealedAnswers && (
  <Button variant="contained" color="primary" onClick={handleRevealAnswers} style={{ marginTop: 10 }}>
    Reveal Answers
  </Button>
)}

                    {revealedAnswers && score === correctAnswers.length && (
                      <Typography variant="h6" style={{ color: "green", fontWeight: "bold", marginTop: 10 }}>
                        🎉 Well done! You got all the answers correct!
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Footer buttons */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <Button variant="contained" color="secondary" onClick={() => handleNavigation("/feedback")}>
                  Report Issue
                </Button>

                <Button
  variant="contained"
  color="secondary"
  onClick={async () => {
    await handleFinishReading();
    resetForm();
    backToDashboard(); // ✅ student → /student-dashboard
  }}
>
  Return to Dashboard
</Button>

              </div>
            </CardContent>
          </Card>
        )}


        {/* Word definition modal (6-9+ only) */}
{definitionOpen && formValues.ageGroup !== "3-5 years old" && (
  <Modal open={definitionOpen} onClose={() => setDefinitionOpen(false)}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "90%", sm: 520 },
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 24,
        p: 3,
        outline: "none",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        Meaning: {selectedWord || ""}
      </Typography>

      {loadingDefinition && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Looking it up…
        </Typography>
      )}

      {!loadingDefinition && definitionError && (
        <Typography variant="body2" sx={{ color: "#b91c1c", mb: 2 }}>
          {definitionError}
        </Typography>
      )}

      {!loadingDefinition && wordDefinition && (
        <>
          {wordDefinition.partOfSpeech ? (
            <Typography variant="body2" sx={{ color: "#555", mb: 1 }}>
              <strong>Type:</strong> {wordDefinition.partOfSpeech}
            </Typography>
          ) : null}

          {wordDefinition.definition ? (
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Definition:</strong> {wordDefinition.definition}
            </Typography>
          ) : null}

          {wordDefinition.example ? (
            <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
              <strong>Example:</strong> “{wordDefinition.example}”
            </Typography>
          ) : null}

          {Array.isArray(wordDefinition.synonyms) && wordDefinition.synonyms.length > 0 ? (
            <Typography variant="body2" sx={{ color: "#555" }}>
              <strong>Similar words:</strong> {wordDefinition.synonyms.join(", ")}
            </Typography>
          ) : null}
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <Button variant="contained" onClick={() => setDefinitionOpen(false)}>
          Close
        </Button>
      </div>
    </Box>
  </Modal>
)}

        {/* Manage recordings modal */}
        {showManageRecordingsModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                position: "relative",
                backgroundColor: "white",
                padding: 20,
                borderRadius: 10,
                width: 400,
                maxHeight: "80vh",
                overflowY: "auto",
                color: "black",
              }}
            >
              <button
                onClick={() => setShowManageRecordingsModal(false)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                }}
                aria-label="Close"
              >
                ❌
              </button>

              <h2 style={{ marginTop: 10 }}>Manage Your Recordings</h2>
              <p>You have reached the maximum of 3 recordings. Please delete one to continue.</p>

              {recordings.map((rec, idx) => (
                <div key={idx} style={{ marginBottom: 10, borderBottom: "1px solid #ccc", paddingBottom: 10 }}>
                  <p>
                    <strong>{rec.storyTitle || rec.fileName}</strong>
                  </p>
                  <audio controls src={rec.url} style={{ width: "100%" }} />
                  <button
                    style={{
                      marginTop: 8,
                      backgroundColor: "#ff4d4d",
                      color: "white",
                      padding: "6px 12px",
                      border: "none",
                      borderRadius: 5,
                      cursor: "pointer",
                    }}
                    onClick={() => handleDeleteRecording(rec)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

        {/* Disclaimer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 40,
            padding: 20,
            backgroundColor: "#111",
            color: "#ccc",
            borderTop: "1px solid #333",
          }}
        >
          <Typography variant="body2" style={{ fontSize: 13 }}>
            ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using
          AI and are intended for entertainment and educational purposes only.
          While we strive for accuracy, some content may contain factual or
          narrative inconsistencies.
          <p style={{ fontSize: "0.8rem", color: "#e11d48", marginTop: "10px" }}>
  <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety filters to ensure all stories 
  remain age-appropriate. Violent, explicit, discriminatory, or harmful content is automatically 
  removed or rewritten. Minor variance may occur due to the nature of AI generation.
</p>
          </Typography>
        </div>
      </Container>
    </div>
  );
};

export default StoryApp;
